/**
 * Condition Evaluator
 *
 * Client-side evaluation engine for targeting conditions.
 * Uses the shared condition registry for evaluation logic.
 * Server-side conditions (page, post, user_role, user_logged_in) are skipped.
 * All evaluation functions are async to support lazy-loading of dependencies.
 */

import { conditionRegistry } from '../../shared/targeting';
import type {
	TargetingCondition,
	TargetingGroup,
	EvaluationContext,
	ConditionEvaluationResult,
	GroupEvaluationResult,
	TargetingEvaluationResult,
} from './types';

// Re-export time utilities from shared module for backwards compatibility
// Note: detectDevice, detectBrowser, detectOS are now  and exported from ua-parser
export {
	getDayOfWeek,
	parseTimeToMinutes,
	getCurrentTimeInMinutes,
} from '../../shared/targeting';

// Re-export async detection functions from ua-parser
export {
	detectDevice,
	detectBrowser,
	detectOS,
} from '../../shared/targeting/ua-parser';

// ============================================================================
// Main Evaluation Functions (all async)
// ============================================================================

/**
 * Evaluate a single condition using the registry
 */
export async function evaluateCondition(
	condition: TargetingCondition,
	context: EvaluationContext
): Promise<ConditionEvaluationResult> {
	return conditionRegistry.evaluateCondition(condition, context);
}

/**
 * Evaluate a group of conditions
 */
export async function evaluateGroup(
	group: TargetingGroup,
	context: EvaluationContext
): Promise<GroupEvaluationResult> {
	// Skip disabled groups
	if (group.enabled === false) {
		return {
			groupId: group.id,
			groupName: group.name,
			matched: false,
			logic: group.conditionLogic || 'AND',
			conditionResults: [],
		};
	}

	const logic = group.conditionLogic || 'AND';
	const conditionResults: ConditionEvaluationResult[] = [];

	for (const condition of group.conditions) {
		const result = await evaluateCondition(condition, context);
		conditionResults.push(result);

		// Short-circuit evaluation
		if (logic === 'AND' && !result.matched && !result.skipped) {
			// AND logic: if any condition fails, group fails
			return {
				groupId: group.id,
				groupName: group.name,
				matched: false,
				logic,
				conditionResults,
			};
		}

		if (logic === 'OR' && result.matched) {
			// OR logic: if any condition passes, group passes
			return {
				groupId: group.id,
				groupName: group.name,
				matched: true,
				logic,
				conditionResults,
			};
		}
	}

	// All conditions evaluated
	const matched =
		logic === 'AND'
			? conditionResults.every((r) => r.matched || r.skipped)
			: conditionResults.some((r) => r.matched);

	return {
		groupId: group.id,
		groupName: group.name,
		matched,
		logic,
		conditionResults,
	};
}

/**
 * Evaluate all targeting groups
 * Groups are combined with OR logic (any group matching = overall match)
 */
export async function evaluateTargeting(
	groups: TargetingGroup[],
	context: EvaluationContext
): Promise<TargetingEvaluationResult> {
	// No groups = no targeting = show to everyone
	if (groups.length === 0) {
		return {
			matched: true,
			groupResults: [],
			pendingServerConditions: [],
			errors: [],
		};
	}

	const groupResults: GroupEvaluationResult[] = [];
	const pendingServerConditions: TargetingCondition[] = [];
	const pendingConditionIds = new Set<string>();
	const errors: string[] = [];

	// Get AJAX-required condition types from registry (non-cacheable server conditions)
	// Cacheable server conditions (specific_posts, content_type, etc.) are already
	// evaluated by PHP at page load - we only need to verify non-cacheable ones via AJAX
	const ajaxRequiredConditionTypes =
		conditionRegistry.getAjaxRequiredConditions();

	for (const group of groups) {
		const result = await evaluateGroup(group, context);
		groupResults.push(result);

		// Collect only AJAX-required conditions (non-cacheable server conditions)
		// Cacheable conditions like specific_posts are already evaluated by PHP
		for (const condition of group.conditions) {
			if (
				ajaxRequiredConditionTypes.includes(condition.type) &&
				condition.enabled !== false &&
				!pendingConditionIds.has(condition.id)
			) {
				pendingServerConditions.push(condition);
				pendingConditionIds.add(condition.id);
			}
		}

		// OR logic between groups: if any group matches, overall matches
		if (result.matched) {
			return {
				matched: true,
				groupResults,
				pendingServerConditions,
				errors,
			};
		}
	}

	// No groups matched
	return {
		matched: false,
		groupResults,
		pendingServerConditions,
		errors,
	};
}

/**
 * Create an evaluation context from the current browser environment
 */
export function createBrowserContext(): EvaluationContext {
	// Parse cookies
	const cookies: Record<string, string> = {};
	document.cookie.split(';').forEach((cookie) => {
		const [name, value] = cookie.trim().split('=');
		if (name) {
			cookies[name] = decodeURIComponent(value || '');
		}
	});

	return {
		url: new URL(window.location.href),
		referrer: document.referrer,
		userAgent: navigator.userAgent,
		now: new Date(),
		cookies,
		windowContext: window,
	};
}
